import React, {
  useContext, useState, KeyboardEvent, useEffect
} from 'react'
import styled from 'styled-components'
import {
  space, SpaceProps, compose, color
} from 'styled-system'
import { GridItem } from '@zuto/core/src/grid'
import Box from '@zuto/core/src/box'
import { ArrowRight } from '@zuto/icons'
import { Button, Checkbox, TermsAndConditions } from '@zuto/atoms'
import QuestionComponentProps from '../../../../Questions/question-component-props'
import { logMarketingPreferences, logPrefilledAnswersThatHaveBeenChangedAgain, logAddedQuestionKeys } from '../../../../utils/PrefillReviewPageApplicationForm-utils'
import withFade from '../../../HOC/withFade'
import withQuestion from '../../../HOC/withQuestion'
import { questionFontStyle } from '../../../Questions/utils'
import { ApplicationFormContext, MarketingPreferencesType } from '../../ApplicationFormContext'
import { PrefillReviewPageApplicationFormContext } from '../../PrefillReviewPageApplicationFormContext'
import { checkAreRequiredFieldsValid } from '../../../../utils/RequiredDetailsPageApplicationForm-utils'


const StyledDiv = styled.div<SpaceProps>`
display: flex;
flex-wrap: wrap;
  ${space}
  `
const StyledCheckbox = styled(Checkbox)`
flex-basis: 35%;
`

const ThankYouMessage = styled(Box)`
a {
    text-decoration: none;
    ${(props) => compose(color)({
  ...props,
  color: 'secondary.1'
})}
  }
`

const StyledContinueButton = styled(Button)`
  justify-content: center;
  text-align: center;
  svg {
    margin: 0px;
  }
`

const Thankyou = ({
  prefillData,
  initialAnswerState,
  requiredFields
}: QuestionComponentProps): JSX.Element => {
  const appFormContext = useContext(ApplicationFormContext)
  const { experimentName } = useContext(PrefillReviewPageApplicationFormContext)
  const [marketingPreferences, setMarketingPreferences] = useState<MarketingPreferencesType>(appFormContext.getAppForm().marketingPreferences)
  const [canContinue, setCanContinue] = useState(false)

  useEffect(() => {
    setCanContinue(checkAreRequiredFieldsValid(prefillData?.requiredDetails, requiredFields))
  }, [prefillData])

  const message = "As part of our service to you we'll contact you about your application to keep you updated. We want you to be the first to know about offers and other relevant information from Zuto. This will be related to vehicle finance or your vehicle, with occasional information from our <a href='/privacy/#our-partners' target='_blank'>partners</a>. Let us know how you'd like us to contact you:"

  const submit = () => {
    logMarketingPreferences(marketingPreferences, experimentName)

    if (prefillData && initialAnswerState) {
      logPrefilledAnswersThatHaveBeenChangedAgain(initialAnswerState, prefillData, experimentName)
      logAddedQuestionKeys(initialAnswerState, prefillData, experimentName)
    }
    appFormContext.dispatch({
      type: 'SUBMIT',
      marketingPreferences: {
        ...marketingPreferences,
        consentText: message
      }
    })
  }

  const updateMarketingPreferences = (key: string, val: boolean) => {
    setMarketingPreferences((prev) => ({
      ...prev,
      [key]: val
    }))
  }

  const handleEnterKeyPress = (e: KeyboardEvent) => {
    if (e.code === 'Enter') {
      submit()
    }
  }

  return (
    <>
      <GridItem gridColumn={['auto / span 12', '3 / span 8']} mt={0}>
        <Box
          textStyle={questionFontStyle}
          mt={4}
          mb={2}
        >
          One last thing
        </Box>
        <ThankYouMessage
          textStyle="body.smallPrint.regular"
          mb={5}
          dangerouslySetInnerHTML={{
            __html: message
          }}
        />
        <StyledDiv mb={5}>
          <Box textStyle="material.body.medium.light" mb={4} minWidth="100%">
            Contact me by
          </Box>
          <StyledCheckbox
            id="tag_email-checkbox"
            data-testid="email-checkbox"
            name="email"
            onKeyPress={handleEnterKeyPress}
            defaultState={marketingPreferences.email}
            valueChanged={(val) => updateMarketingPreferences('email', !!val)}
          >
            Email
          </StyledCheckbox>
          <StyledCheckbox
            id="tag_sms-checkbox"
            data-testid="sms-checkbox"
            name="sms"
            onKeyPress={handleEnterKeyPress}
            defaultState={marketingPreferences.sms}
            valueChanged={(val) => updateMarketingPreferences('sms', !!val)}
          >
            SMS
          </StyledCheckbox>
        </StyledDiv>
        <TermsAndConditions mb={5} mt={5}>
          By completing this application you agree to our
          {' '}
          <a href="/terms-and-conditions/" target="_blank">terms and conditions</a>
          {' '}
          and
          {' '}
          <a href="/privacy/" target="_blank">privacy policy</a>
          .
          <br />
          <br />
          We won&apos;t charge you anything for our service but we&apos;ll
          receive a commission from the lender which varies based
          on the product or amount borrowed. This doesn&apos;t
          affect the amount you pay.
        </TermsAndConditions>
      </GridItem>
      <GridItem gridColumn={['1 / span 12', '3 / span 8']} mt={0} mb={[5, 4]}>
        <StyledContinueButton
          disabled={prefillData && !canContinue}
          icon={<ArrowRight />}
          data-testid="continue-button-center"
          size="large"
          subText={'No impact on your credit score'}
          onClick={submit}
          name="submit-button"
          id="centered-submit-button"
          variant={'prominentColourScheme'}
        >
          Get a quote
        </StyledContinueButton>
      </GridItem>
    </>
  )
}

export default withFade(withQuestion(Thankyou))
